import { useMapsLibrary } from "@vis.gl/react-google-maps";
import React, { useCallback, useMemo } from "react";
import useAuth from "../hooks/useAuth";
import { radiusZoomLevel } from "../utils/staticMethods";

const CommonMapContext = React.createContext({});

const CommonMapProvider = ({ children }) => {
  const { user } = useAuth();
  const [map, setMap] = React.useState(null);
  const [center, setCenter] = React.useState();
  const [zoom, setZoom] = React.useState();
  const [heading, setHeading] = React.useState();
  const [current, setCurrent] = React.useState(null);
  const [selectedFloorplan, setSelectedFloorplan] = React.useState(null);
  const [floorplanImage, setFloorplanImage] = React.useState(null);
  const [editFloorplanEnabled, setEditFloorplanEnabled] = React.useState(false);
  const [focusedObject, setFocusedObject] = React.useState(null);
  const [following, setFollowing] = React.useState(user.defaultFollow);
  const [showInfoIds, setShowInfoIds] = React.useState([]);
  const [clusterer, setClusterer] = React.useState(null);

  const zonesMarkersRef = React.useRef(new Map());
  const markersStateRef = React.useRef({ items: new Map(), groups: new Map() });
  const markerClustererRef = React.useRef();

  const mapLibCore = useMapsLibrary("core");
  const mapLibMaps = useMapsLibrary("maps");
  const mapLibMarker = useMapsLibrary("marker");
  const mapLibGeometry = useMapsLibrary("geometry");
  const mapLibPlaces = useMapsLibrary("places");

  const getZoomLevelForGeometry = useCallback(
    (geometry) => {
      let zoom = 14;
      let d;
      if (geometry.type === "circle") {
        d = geometry.radius * 2;
      }
      if (geometry.type === "polygon") {
        let v = geometry.getPath();
        let bounds = new mapLibCore.LatLngBounds();
        for (let i = 0; i < v.getLength(); i++) {
          bounds.extend(v.getAt(i));
        }
        let sw = bounds.getSouthWest();
        let ne = bounds.getNorthEast();
        // let distance = mapLibGeometry.spherical.computeDistanceBetween(
        //   bounds.getSouthWest(),
        //   bounds.getNorthEast()
        // );
        //bounds;
        d = Number(
          //Math.max(Math.abs(sw.lat() - ne.lat()), Math.abs(sw.lng() - ne.lng()))
          Math.max(
            mapLibGeometry.spherical.computeDistanceBetween(
              { lat: ne.lat(), lng: ne.lng() },
              { lat: ne.lat(), lng: sw.lng() }
            ),
            mapLibGeometry.spherical.computeDistanceBetween(
              { lat: ne.lat(), lng: ne.lng() },
              { lat: sw.lat(), lng: ne.lng() }
            )
          )
        );
        // d = Number(distance) / 2;
      }

      if (d) {
        let entries = Object.entries(radiusZoomLevel).toSorted((a, b) => {
          return b[1] - a[1];
        });
        let value = entries[0][1];
        let index = "";
        for (let i = 0; i < entries.length; i++) {
          let v = Math.abs(entries[i][1] - d);
          if (v < value) {
            value = v;
            index = entries[i][0];
          }
        }
        zoom = Number(index);
      }

      return zoom;
    },
    [mapLibCore, mapLibGeometry]
  );

  const value = useMemo(() => {
    return {
      map,
      mapLibCore,
      mapLibMaps,
      mapLibMarker,
      mapLibGeometry,
      mapLibPlaces,
      setMap,
      center,
      setCenter,
      zoom,
      setZoom,
      heading,
      setHeading,
      current,
      setCurrent,
      selectedFloorplan,
      setSelectedFloorplan,
      floorplanImage,
      setFloorplanImage,
      focusedObject,
      setFocusedObject,
      editFloorplanEnabled,
      setEditFloorplanEnabled,
      following,
      setFollowing,
      getZoomLevelForGeometry,
      showInfoIds,
      setShowInfoIds,
      zonesMarkersRef,
      markersStateRef,
      markerClustererRef,
      clusterer,
      setClusterer,
    };
  }, [
    map,
    mapLibCore,
    mapLibMaps,
    mapLibMarker,
    mapLibGeometry,
    mapLibPlaces,
    center,
    zoom,
    heading,
    current,
    selectedFloorplan,
    floorplanImage,
    focusedObject,
    editFloorplanEnabled,
    following,
    showInfoIds,
    clusterer,
    getZoomLevelForGeometry,
  ]);

  return (
    <CommonMapContext.Provider value={value}>
      {children}
    </CommonMapContext.Provider>
  );
};

export { CommonMapProvider, CommonMapContext };
